import { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Link as LinkIcon,
  Type,
  Wifi,
  Mail,
  Phone,
  MessageSquare,
  Download,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { addHistoryItem } from "../lib/qrHistory";

const TYPES = [
  { id: "url", label: "URL", icon: LinkIcon },
  { id: "text", label: "Metin", icon: Type },
  { id: "wifi", label: "Wi-Fi", icon: Wifi },
  { id: "email", label: "E-posta", icon: Mail },
  { id: "phone", label: "Telefon", icon: Phone },
  { id: "sms", label: "SMS", icon: MessageSquare },
];

function escapeWifi(s) {
  return String(s).replace(/([\\;,:"])/g, "\\$1");
}

function buildQrValue(t, f) {
  switch (t) {
    case "url":
      return f.url.trim();
    case "text":
      return f.text;
    case "wifi":
      if (!f.ssid) return "";
      return `WIFI:T:${f.encryption};S:${escapeWifi(f.ssid)};P:${escapeWifi(
        f.password || ""
      )};${f.hidden ? "H:true;" : ""};`;
    case "email": {
      if (!f.email) return "";
      const params = [];
      if (f.subject) params.push(`subject=${encodeURIComponent(f.subject)}`);
      if (f.body) params.push(`body=${encodeURIComponent(f.body)}`);
      return `mailto:${f.email}${params.length ? `?${params.join("&")}` : ""}`;
    }
    case "phone":
      return f.phone ? `tel:${f.phone.replace(/\s+/g, "")}` : "";
    case "sms":
      if (!f.smsPhone) return "";
      return `SMSTO:${f.smsPhone.replace(/\s+/g, "")}:${f.smsMessage || ""}`;
    default:
      return "";
  }
}

export default function QRGenerator({ onHistoryChange }) {
  const [type, setType] = useState("url");
  const [fields, setFields] = useState({
    url: "",
    text: "",
    ssid: "",
    password: "",
    encryption: "WPA",
    hidden: false,
    email: "",
    subject: "",
    body: "",
    phone: "",
    smsPhone: "",
    smsMessage: "",
  });
  const canvasWrapRef = useRef(null);
  const lastSaveRef = useRef({ value: "", at: 0 });

  const setField = (key, value) =>
    setFields((prev) => ({ ...prev, [key]: value }));

  const qrValue = useMemo(() => buildQrValue(type, fields), [type, fields]);

  const isEmpty = !qrValue;

  const downloadQR = () => {
    if (isEmpty) return;
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${type}-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR kod indirildi");
  };

  const saveToHistory = () => {
    if (isEmpty) return;
    const now = Date.now();
    if (
      lastSaveRef.current.value === qrValue &&
      now - lastSaveRef.current.at < 1000
    ) {
      return;
    }
    lastSaveRef.current = { value: qrValue, at: now };
    const saved = addHistoryItem({ kind: "generate", type, value: qrValue });
    onHistoryChange && onHistoryChange(saved);
    toast.success("Geçmişe kaydedildi");
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Type selector chips */}
      <div
        data-testid="generator-type-selector"
        className="flex gap-2 overflow-x-auto scroll-thin pb-1 -mx-1 px-1"
      >
        {TYPES.map(({ id, label, icon: Icon }) => {
          const active = type === id;
          return (
            <button
              key={id}
              data-testid={`generate-type-${id}-chip`}
              onClick={() => setType(id)}
              className={`shrink-0 inline-flex items-center gap-2 rounded-full border px-4 h-10 text-sm font-semibold transition-all active:scale-95 ${
                active
                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:text-blue-700"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              {label}
            </button>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="flex flex-col gap-4">
          {type === "url" && (
            <Field label="Bağlantı (URL)">
              <Input
                data-testid="input-url"
                placeholder="https://ornek.com"
                value={fields.url}
                onChange={(e) => setField("url", e.target.value)}
                className="rounded-xl h-12 bg-white"
              />
            </Field>
          )}
          {type === "text" && (
            <Field label="Metin">
              <Textarea
                data-testid="input-text"
                placeholder="QR kodda gösterilecek metin"
                value={fields.text}
                onChange={(e) => setField("text", e.target.value)}
                className="rounded-xl min-h-[120px] bg-white"
              />
            </Field>
          )}
          {type === "wifi" && (
            <>
              <Field label="Ağ Adı (SSID)">
                <Input
                  data-testid="input-wifi-ssid"
                  placeholder="EvAgim"
                  value={fields.ssid}
                  onChange={(e) => setField("ssid", e.target.value)}
                  className="rounded-xl h-12 bg-white"
                />
              </Field>
              <Field label="Parola">
                <Input
                  data-testid="input-wifi-password"
                  type="text"
                  placeholder="••••••••"
                  value={fields.password}
                  onChange={(e) => setField("password", e.target.value)}
                  className="rounded-xl h-12 bg-white"
                />
              </Field>
              <Field label="Şifreleme">
                <div className="flex gap-2">
                  {["WPA", "WEP", "nopass"].map((enc) => (
                    <button
                      key={enc}
                      data-testid={`wifi-enc-${enc.toLowerCase()}`}
                      type="button"
                      onClick={() => setField("encryption", enc)}
                      className={`flex-1 h-11 rounded-xl border text-sm font-semibold transition-all active:scale-95 ${
                        fields.encryption === enc
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                      }`}
                    >
                      {enc === "nopass" ? "Açık" : enc}
                    </button>
                  ))}
                </div>
              </Field>
            </>
          )}
          {type === "email" && (
            <>
              <Field label="E-posta Adresi">
                <Input
                  data-testid="input-email-address"
                  type="email"
                  placeholder="ornek@mail.com"
                  value={fields.email}
                  onChange={(e) => setField("email", e.target.value)}
                  className="rounded-xl h-12 bg-white"
                />
              </Field>
              <Field label="Konu (opsiyonel)">
                <Input
                  data-testid="input-email-subject"
                  value={fields.subject}
                  onChange={(e) => setField("subject", e.target.value)}
                  className="rounded-xl h-12 bg-white"
                />
              </Field>
              <Field label="Mesaj (opsiyonel)">
                <Textarea
                  data-testid="input-email-body"
                  value={fields.body}
                  onChange={(e) => setField("body", e.target.value)}
                  className="rounded-xl min-h-[100px] bg-white"
                />
              </Field>
            </>
          )}
          {type === "phone" && (
            <Field label="Telefon Numarası">
              <Input
                data-testid="input-phone"
                type="tel"
                placeholder="+90 555 000 00 00"
                value={fields.phone}
                onChange={(e) => setField("phone", e.target.value)}
                className="rounded-xl h-12 bg-white"
              />
            </Field>
          )}
          {type === "sms" && (
            <>
              <Field label="Telefon Numarası">
                <Input
                  data-testid="input-sms-phone"
                  type="tel"
                  placeholder="+90 555 000 00 00"
                  value={fields.smsPhone}
                  onChange={(e) => setField("smsPhone", e.target.value)}
                  className="rounded-xl h-12 bg-white"
                />
              </Field>
              <Field label="Mesaj">
                <Textarea
                  data-testid="input-sms-message"
                  value={fields.smsMessage}
                  onChange={(e) => setField("smsMessage", e.target.value)}
                  className="rounded-xl min-h-[100px] bg-white"
                />
              </Field>
            </>
          )}
        </div>

        {/* Preview */}
        <div className="flex flex-col gap-4">
          <div
            ref={canvasWrapRef}
            className="relative rounded-2xl bg-white border border-slate-200 p-6 flex items-center justify-center aspect-square"
            data-testid="qr-preview"
          >
            {isEmpty ? (
              <div className="text-center text-slate-400 max-w-[220px]">
                <div className="mx-auto w-14 h-14 rounded-2xl border-2 border-dashed border-slate-300 flex items-center justify-center mb-3">
                  <Type className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <p className="text-sm">
                  Önizleme için sol taraftaki alanları doldurun.
                </p>
              </div>
            ) : (
              <QRCodeCanvas
                value={qrValue}
                size={260}
                level="M"
                marginSize={2}
                fgColor="#0F172A"
                bgColor="#FFFFFF"
              />
            )}
          </div>

          <div className="flex gap-3">
            <Button
              data-testid="qr-download-button"
              disabled={isEmpty}
              onClick={downloadQR}
              className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white h-12 font-semibold active:scale-95 transition-all"
            >
              <Download className="w-4 h-4 mr-2" />
              İndir (PNG)
            </Button>
            <Button
              data-testid="qr-save-history-button"
              disabled={isEmpty}
              onClick={saveToHistory}
              variant="outline"
              className="rounded-full h-12 font-semibold border-slate-300 active:scale-95 transition-all"
            >
              <Save className="w-4 h-4 mr-2" />
              Kaydet
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs tracking-[0.05em] uppercase font-bold text-slate-500">
        {label}
      </Label>
      {children}
    </div>
  );
}
